import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "~/context/AuthContext";
import { apiClient } from "~/lib/api-client";
import type { Subscription } from "~/types/BillingTypes";

interface SubscriptionContextType {
  subscription: Subscription | null;
  isLoading: boolean;
  error: string | null;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const teamId = user?.team_id ?? null;
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadSubscription = useCallback(async () => {
    const requestId = ++requestIdRef.current;

    if (!teamId) {
      setSubscription(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get("/billing/subscription");
      if (requestId !== requestIdRef.current) return;
      if (response.status === 200) {
        setSubscription(response.data?.data || null);
      }
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to load subscription:", err);
      setError("Failed to load subscription data");
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [teamId]);

  useEffect(() => {
    setSubscription(null);
    void loadSubscription();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        isLoading,
        error,
        refreshSubscription: loadSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within SubscriptionProvider");
  }
  return context;
}
